import { useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import MDPagination from "components/MDPagination";

const ITEM_PX = 40;
const NAV_SLOTS = 2;

export function getVisiblePages(total, current, maxItems) {
  const totalPage = Math.max(1, Number(total) || 1);
  const currentPage = Math.min(totalPage, Math.max(1, Number(current) || 1));
  const limit = Math.max(5, Number(maxItems) || 5);

  if (totalPage <= limit) {
    return Array.from({ length: totalPage }, (_, index) => index + 1);
  }

  const pages = new Set([1, totalPage, currentPage]);
  let left = currentPage - 1;
  let right = currentPage + 1;

  const itemCount = () => {
    const nums = [...pages].sort((a, b) => a - b);
    let extra = 0;
    for (let i = 1; i < nums.length; i += 1) {
      if (nums[i] - nums[i - 1] > 1) extra += 1;
    }
    return nums.length + extra;
  };

  while (itemCount() < limit) {
    let added = false;
    if (left >= 1 && itemCount() < limit) {
      pages.add(left);
      left -= 1;
      added = true;
    }
    if (right <= totalPage && itemCount() < limit) {
      pages.add(right);
      right += 1;
      added = true;
    }
    if (!added) break;
  }

  const nums = [...pages].sort((a, b) => a - b);
  const out = [];
  nums.forEach((page, index) => {
    if (index > 0 && page - nums[index - 1] > 1) {
      out.push(`ellipsis-${nums[index - 1]}`);
    }
    out.push(page);
  });
  return out;
}

function MDPageBar({ totalPage, currentPage, onChange, variant, color }) {
  const wrapRef = useRef(null);
  const [maxItems, setMaxItems] = useState(12);

  useLayoutEffect(() => {
    const node = wrapRef.current;
    if (!node) return undefined;

    const update = () => {
      const width = node.clientWidth || 0;
      const fit = Math.floor(width / ITEM_PX) - NAV_SLOTS;
      setMaxItems(Math.max(5, fit));
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  if (!totalPage || totalPage < 1) return null;

  const pages = getVisiblePages(totalPage, currentPage, maxItems);

  return (
    <div
      ref={wrapRef}
      style={{
        width: "100%",
        minWidth: 0,
        flex: 1,
        overflow: "hidden",
      }}
    >
      <MDPagination variant={variant} color={color}>
        {currentPage > 1 && totalPage > 1 ? (
          <MDPagination item onClick={() => onChange(currentPage - 1)}>
            <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
          </MDPagination>
        ) : null}
        {pages.map((page) =>
          typeof page === "string" ? (
            <span
              key={page}
              style={{
                padding: "0 2px",
                minWidth: 16,
                textAlign: "center",
                color: "#94a3b8",
                userSelect: "none",
                fontSize: 14,
                lineHeight: "36px",
              }}
            >
              …
            </span>
          ) : (
            <MDPagination
              item
              key={page}
              onClick={() => onChange(page)}
              active={currentPage === page}
            >
              {page}
            </MDPagination>
          )
        )}
        {currentPage < totalPage ? (
          <MDPagination item onClick={() => onChange(currentPage + 1)}>
            <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
          </MDPagination>
        ) : null}
      </MDPagination>
    </div>
  );
}

MDPageBar.defaultProps = {
  totalPage: 1,
  currentPage: 1,
  variant: "gradient",
  color: "info",
};

MDPageBar.propTypes = {
  totalPage: PropTypes.number,
  currentPage: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(["gradient", "contained"]),
  color: PropTypes.string,
};

export default MDPageBar;
